import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useDataStore } from '@/stores/dataStore';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { parseAttachments } from '@/components/AttachmentList';
import { buildForwardBody } from '@/lib/forwarded';
import { buildMessageLink } from '@/lib/messageLink';
import { useLongPress } from '@/lib/useLongPress';
import type { Message } from '@/types/api';

/**
 * Right-click / long-press context menu for a message row, plus the three
 * clipboard/vault actions it offers. The menu's open position is state here;
 * the row renders <MessageContextMenu> from it and wires the remaining
 * (prop-backed) actions — reply/edit/pin/react/delete — itself.
 *
 *  - copyText  — message body to the clipboard.
 *  - copyLink  — a pure deep-link pointer (no embedded invite) to the message.
 *  - saveToVault — forward into the personal Сейф (self-chat): my own message
 *    saved as-is, someone else's kept attributed via the forward wire-format.
 */
export function useMessageContextMenu(message: Message, mine: boolean) {
  const { t } = useTranslation();
  const m = message;
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  function openContextMenuAt(x: number, y: number) {
    setContextMenu({ x, y });
  }
  function openContextMenu(e: React.MouseEvent) {
    // Some inner elements (links, images, embeds) can swallow the
    // bubbling contextmenu event in older builds — using stopPropagation
    // up the tree was the old footgun. We don't add stopPropagation
    // here, just preventDefault on this row, then schedule the menu.
    e.preventDefault();
    openContextMenuAt(e.clientX, e.clientY);
  }
  // Touch long-press → same context menu as desktop right-click. Spread onto
  // the row (mouse `onContextMenu` stays intact for desktop).
  const longPress = useLongPress((x, y) => openContextMenuAt(x, y));
  function closeContextMenu() {
    setContextMenu(null);
  }

  async function copyText() {
    try {
      await navigator.clipboard.writeText(m.content);
      toast.success(t('messages.textCopied'));
    } catch {
      toast.error(t('messages.copyError'));
    }
  }
  async function copyLink() {
    // A pure pointer — no embedded invite. It jumps to the message for anyone
    // who already has access; bringing someone onto the server is the explicit
    // "Пригласить на сервер" action (a scoped invite), never a side effect of
    // copying a link.
    const link = buildMessageLink({
      guildId: useDataStore.getState().currentGuildId,
      channelId: m.channelId,
      messageId: m.id,
    });
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('messages.linkCopied'));
    } catch {
      toast.error(t('messages.linkCopyError'));
    }
  }
  async function saveToVault() {
    try {
      const vault = await api.openSavedVault();
      useDataStore.getState().upsertDmChannel(vault);
      const attachmentIds = parseAttachments(m.attachments).map((a) => a.id);
      // My own message → save the text as-is. Someone else's → keep the original
      // author via the forward wire-format, which the vault renders as a quiet
      // "от {author}" line (compactForward) — no big "forwarded" card.
      const content = mine
        ? m.content
        : buildForwardBody(
            {
              authorName: m.author.displayName || m.author.username,
              sourceChannelLabel: '',
              content: m.content,
              authorId: m.author.id,
              authorAvatarUrl: m.author.avatarUrl,
            },
            t('forward.someone'),
          );
      await api.sendMessage(vault.id, {
        content,
        ...(attachmentIds.length > 0 ? { forwardUploadIds: attachmentIds, forwardFromMessageId: m.id } : {}),
      });
      toast.success(t('messages.savedToVault'));
    } catch (err) {
      toast.error(...errToToast(err, t('messages.saveError')));
    }
  }

  return { contextMenu, openContextMenu, openContextMenuAt, longPress, closeContextMenu, copyText, copyLink, saveToVault };
}
